const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'jfif'];
const VIDEO_EXTS = ['mp4', 'mov'];
const AUDIO_EXTS = ['mp3'];
const DOC_EXTS = ['pdf', 'pptx', 'eps', 'mproj', 'txt', 'ai'];

export const getTypeByExt = (ext) => {
  const normalized = String(ext || '').toLowerCase().trim();

  if (IMAGE_EXTS.includes(normalized)) return 'image';
  if (VIDEO_EXTS.includes(normalized)) return 'video';
  if (AUDIO_EXTS.includes(normalized)) return 'audio';
  if (DOC_EXTS.includes(normalized)) return 'document';
  return 'other';
};

export const fixMojibake = (value) => {
  if (!value || typeof value !== 'string') return value;

  // Attempt to recover UTF-8 text that was decoded as Latin-1.
  if (!/[ØÙÃÂ]/.test(value)) return value;

  try {
    const bytes = Uint8Array.from(value, (c) => c.charCodeAt(0) & 0xff);
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    if (decoded.includes('ï¿½')) return value;
    return decoded;
  } catch {
    return value;
  }
};

export const buildPublicUrl = (path) => {
  if (!path || typeof path !== 'string') return '';
  if (/^(https?:)?\/\//i.test(path)) return path;

  const normalizedPath = path.replace(/\\/g, '/').replace(/^\/+/, '');
  const encodedPath = normalizedPath
    .split('/')
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '/');
  return `${base}${encodedPath}`;
};

export const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return 'غير متاح';
  if (bytes === 0) return '0 بايت';

  const units = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 ? 1 : 2)} ${units[index]}`;
};

export const formatLabel = (filename) => {
  const base = filename.replace(/\.[^/.]+$/, '');
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const ARABIC_RE = /[\u0600-\u06ff]/;
const vendorHints = [
  'pngtree',
  'freepik',
  'vecteezy',
  'shutterstock',
  'clip art',
  'clipart',
  'illustration',
  'background',
  '3d',
  'icon set'
];

const isNoisySlug = (value = '') => {
  const text = value.trim().toLowerCase();
  if (!text) return true;

  const hasArabic = ARABIC_RE.test(text);
  const asciiLike = !hasArabic && /^[a-z0-9\s_-]+$/i.test(text);
  const tokenCount = text.split(/[\s_-]+/).filter(Boolean).length;
  const separatorCount = (text.match(/[-_]/g) || []).length;
  const vendorLike = vendorHints.some((hint) => text.includes(hint));
  const genericLike = /^image\s*\d*$/i.test(text) || /^img\s*\d*$/i.test(text);

  if (vendorLike || genericLike) return true;
  if (asciiLike && (text.length > 30 || tokenCount >= 6 || separatorCount >= 3)) return true;
  return false;
};

const typeTitles = {
  image: 'صورة تعليمية',
  video: 'فيديو تعليمي',
  audio: 'تسجيل صوتي',
  document: 'ملف تعليمي',
  other: 'مورد تعليمي'
};

export const buildDisplayName = ({ name, filename, type, sourceLabel, index = 0 }) => {
  const cleanedName = (name || '').trim();
  const cleanedSource = (sourceLabel || '').trim();
  const fallback = typeTitles[type] || typeTitles.other;

  if (cleanedName && !isNoisySlug(cleanedName)) {
    return cleanedName;
  }

  const filenameLabel = formatLabel(filename || '');
  if (filenameLabel && !isNoisySlug(filenameLabel)) {
    return filenameLabel;
  }

  if (cleanedSource && ARABIC_RE.test(cleanedSource) && !isNoisySlug(cleanedSource)) {
    return `${fallback} - ${cleanedSource}`;
  }

  return `${fallback} ${index + 1}`;
};

export const getUsageHint = ({ type, ext }) => {
  if (type === 'image') return 'مناسب للشرح البصري داخل الحصة.';
  if (type === 'video') return 'يفضل تشغيله بعد تمهيد سريع للموضوع.';
  if (type === 'audio') return 'مفيد للتثبيت والمراجعة السمعية.';
  if (ext === 'pdf') return 'مفيد للقراءة أو المراجعة الفردية.';
  if (ext === 'pptx') return 'جاهز للعرض المباشر أمام الطلاب.';
  if (type === 'document') return 'ملف داعم يمكن الرجوع له أثناء الشرح.';
  return 'مادة إضافية مرتبطة بالدرس.';
};

const hasAny = (hay, keywords) => keywords.some((word) => hay.includes(word));

export const describeAsset = ({ name, filename, ext, type }) => {
  const hay = `${name || ''} ${filename || ''}`.toLowerCase();

  if (hasAny(hay, ['السكري', 'sukari', 'ذهب', 'gold'])) {
    return 'مصدر يوضح التعدين واستخراج الذهب في مصر.';
  }

  if (hasAny(hay, ['فوسفات', 'phosphate', 'ابو', 'طرطور'])) {
    return 'مادة داعمة لفهم الفوسفات وأهميته الزراعية.';
  }

  if (hasAny(hay, ['غاز', 'gas', 'بترول', 'طاقة', 'كهرب', 'شمس', 'رياح', 'بنبان', 'ظهر'])) {
    return 'محتوى يشرح مصادر الطاقة في مصر بشكل مبسط.';
  }

  if (hasAny(hay, ['مياه', 'نيل', 'زراعة', 'محاصيل', 'دلتا', 'ري'])) {
    return 'مصدر يربط بين المياه والزراعة في مصر.';
  }

  if (hasAny(hay, ['اهرام', 'أهرام', 'كرنك', 'معابد', 'اثار', 'آثار'])) {
    return 'مادة مرتبطة بالسياحة والهوية التاريخية لمصر.';
  }

  if (ext === 'pptx') return 'عرض تقديمي داعم لشرح الدرس.';
  if (ext === 'pdf') return 'ملف مرجعي للقراءة أو الطباعة.';
  if (type === 'image') return 'صورة توضيحية مرتبطة بموضوع الدرس.';
  if (type === 'video') return 'فيديو قصير يشرح الفكرة بصريًا.';
  if (type === 'audio') return 'ملف صوتي يساعد في المراجعة.';
  if (ext === 'txt') return 'ملف نصي يحتوي ملاحظات أو بيانات مساعدة.';

  return 'ملف داعم داخل المكتبة التعليمية.';
};
